import { Controller } from "@hotwired/stimulus"
import { motionLite } from "../lib/motion"


// Reveal controller: triggers subtle entrance animation via IntersectionObserver
// Usage:
//   data-controller="reveal"
//   data-reveal-delay-value="80" (optional, ms; capped at 150)
//   data-reveal-target="item" for multiple children (optional)
//   data-reveal-delay on individual targets to override base delay
export default class extends Controller {
  static targets = ["item"]

  static values = {
    delay: { type: Number, default: 0 },
    threshold: { type: Number, default: 0.1 },
    rootMargin: { type: String, default: "0px" }
  }

  initialize() {
    this.revealElements = []
  }

  connect() {
    this.revealElements = this.hasItemTarget ? this.itemTargets : [this.element]
    if (!this.revealElements.length) return

    if (this.shouldSkipAnimation()) {
      this.showAll()
      return
    }

    this.observer = new IntersectionObserver(
      this.handleIntersect.bind(this),
      {
        root: null,
        rootMargin: this.rootMarginValue,
        threshold: this.thresholdValue
      }
    )

    this.revealElements.forEach((target) => {
      if (target.dataset.revealState === "complete") {
        return
      }

      target.dataset.revealState = target.dataset.revealState || "pending"
      this.observer.observe(target)
    })
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return

      const target = entry.target
      this.observer.unobserve(target)
      this.playAnimation(target)
    })
  }

  playAnimation(target) {
    if (target.dataset.revealState === "complete") return

    const delay = this.computeDelay(target)
    if (delay > 0) {
      target.style.setProperty("--reveal-delay", `${delay}ms`)
    }

    target.dataset.revealState = "animating"
    target.classList.add("reveal-active")
    target.addEventListener(
      "animationend",
      () => {
        target.dataset.revealState = "complete"
        target.classList.remove("reveal-active")
        target.style.removeProperty("--reveal-delay")
      },
      { once: true }
    )
  }

  computeDelay(target) {
    const rawTargetDelay = Number(target.dataset.revealDelay || target.dataset.revealDelayValue)
    const baseDelay = Number.isFinite(rawTargetDelay) ? rawTargetDelay : this.delayValue

    if (!Number.isFinite(baseDelay) || baseDelay <= 0) {
      return 0
    }

    return Math.min(baseDelay, 150)
  }

  shouldSkipAnimation() {
    return motionLite()
  }

  showAll() {
    (this.revealElements || [this.element]).forEach((target) => {
      target.dataset.revealState = "complete"
      target.classList.remove("reveal-active")
      target.style.removeProperty("--reveal-delay")
    })
  }
}
